'use client';
import React from 'react';
import Link from 'next/link';
import { X, User, LogOut, LogIn } from 'lucide-react';

export interface MobileMenuItem {
  href: string;
  label: string;
}

interface MobileMenuClientProps {
  items: MobileMenuItem[];
  isLoggedIn: boolean;
  loginHref: string;
  logoutHref: string;
  profileHref: string;
}

export default function MobileMenuClient({
  items,
  isLoggedIn,
  loginHref,
  logoutHref,
  profileHref,
}: MobileMenuClientProps) {
  const [open, setOpen] = React.useState(false);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (open && !target.closest('[data-mobile-menu]')) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close menu on escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const handleLinkClick = () => {
    setOpen(false);
  };

  return (
    <div className="lg:hidden" data-mobile-menu>
      {/* Hamburger Button */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Toggle menu"
        aria-expanded={open}
        className="relative z-50 p-2 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/20"
      >
        <div className="relative w-6 h-6">
          {/* Animated hamburger icon */}
          <span
            className={`absolute left-0 top-1 w-6 h-0.5 bg-current transition-all duration-300 ${
              open ? 'rotate-45 translate-y-2' : ''
            }`}
          />
          <span
            className={`absolute left-0 top-2.5 w-6 h-0.5 bg-current transition-all duration-300 ${
              open ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`absolute left-0 top-4 w-6 h-0.5 bg-current transition-all duration-300 ${
              open ? '-rotate-45 -translate-y-2' : ''
            }`}
          />
        </div>
      </button>

      {/* Mobile Menu Overlay */}
      {open && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-gradient-to-br from-white via-white to-paper-beige shadow-2xl z-50 lg:hidden transform transition-transform duration-300 ease-out">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-coral-splash/10 to-leaf-green/10">
              <h2 className="text-xl font-bold text-ink-dark" style={{ fontFamily: 'Georgia, serif' }}>
                Menu
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg text-ink-dark/60 hover:text-coral-splash hover:bg-coral-splash/10 transition-all duration-300"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Menu Content */}
            <div className="flex flex-col h-full">
              {/* Navigation Items */}
              <nav className="flex-1 overflow-y-auto py-6">
                <div className="px-6 space-y-2">
                  {items.map((item, index) => (
                    <Link
                      key={`${item.label}-${index}`}
                      href={item.href}
                      onClick={handleLinkClick}
                      className="group flex items-center gap-4 px-4 py-3 rounded-xl text-ink-dark hover:bg-gradient-to-r hover:from-coral-splash/10 hover:to-leaf-green/10 hover:text-coral-splash transition-all duration-300 transform hover:-translate-y-0.5"
                    >
                      <div className="w-2 h-2 bg-coral-splash/30 rounded-full group-hover:bg-coral-splash transition-colors" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </nav>

              {/* User Section */}
              <div className="border-t border-gray-200 p-6 bg-gradient-to-r from-gray-50 to-white">
                {isLoggedIn ? (
                  <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
                    <Link
                      href={profileHref}
                      onClick={handleLinkClick}
                      className="flex flex-1 items-center justify-center gap-3 px-4 py-3 rounded-xl text-ink-dark hover:bg-coral-splash/10 transition-all duration-300"
                    >
                      <User className="h-5 w-5" />
                      <span className="font-medium">Profile</span>
                    </Link>
                    <Link
                      href={logoutHref}
                      onClick={handleLinkClick}
                      className="flex flex-1 items-center justify-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-300"
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="font-medium">Sign Out</span>
                    </Link>
                  </div>
                ) : (
                  <Link
                    href={loginHref}
                    onClick={handleLinkClick}
                    className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-coral-splash to-coral-splash/90 text-white rounded-xl hover:shadow-watercolor transition-all duration-300 transform hover:-translate-y-0.5 w-full font-semibold"
                  >
                    <LogIn className="h-5 w-5" />
                    <span>Sign In</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}